import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { api } from '../lib/api.js';

// Konteks halaman aktif (pengganti A.route / A.go / A.render / #topActions v1).

export interface Route {
  view: string;
  param: string;
  q: Record<string, string>;
}

export interface ViewCtx {
  r: Route;
  /** Pindah halaman: '/pesan', '/pesanan?tab=aktif' (tautan lama '#/x' juga diterima). Sama dengan halaman sekarang = muat ulang. */
  go: (to: string) => void;
  /** Muat ulang halaman aktif (v1: A.render()). */
  reload: () => void;
}

export const ViewContext = createContext<ViewCtx | null>(null);
export function useView(): ViewCtx {
  const v = useContext(ViewContext);
  if (!v) throw new Error('useView di luar halaman');
  return v;
}

/** '#/x' (tautan lama v1) → '/x'. */
export const normTo = (to: string) => (to.startsWith('#') ? to.slice(1) : to);

/**
 * go/reload yang juga bekerja DI LUAR halaman — untuk hook modal yang dipanggil dari palet pencarian global
 * (SearchProvider ada di luar ViewContext). Di dalam halaman sama dengan useView(); di luar halaman:
 * go = pindah lewat router (tujuan = lokasi sekarang → seperti reload), reload = ambil ulang data halaman aktif
 * (query ['view', …]) — v1 A.go / A.render() selalu bekerja pada halaman aktif.
 */
export function useViewOrFallback(): Pick<ViewCtx, 'go' | 'reload'> {
  const view = useContext(ViewContext);
  const navigate = useNavigate();
  const qc = useQueryClient();
  if (view) return view;
  const reload = () => void qc.invalidateQueries({ queryKey: ['view'] });
  return {
    go: (to) => {
      const t = normTo(to);
      if (t === window.location.pathname + window.location.search) reload();
      else void navigate(t);
    },
    reload,
  };
}

/** Tombol aksi di topbar halaman (v1: $('#topActions').innerHTML = …). */
export function TopActions({ children }: { children: ReactNode }) {
  const el = document.getElementById('topActions');
  return el ? createPortal(children, el) : null;
}

/** Status memuat & gagal halaman — markup sama dengan renderNow() v1. */
export const Loading = () => (
  <div className="empty loading">
    <span className="spin" />
    Memuat…
  </div>
);

export const ViewError = ({ error }: { error: unknown }) => (
  <div className="card">
    <div className="empty danger-txt">{(error as Error)?.message || String(error)}</div>
  </div>
);

/**
 * Data halaman dari API: selalu diambil ulang saat halaman dibuka/dimuat ulang (tanpa cache lama, sama dengan v1).
 * Mengembalikan data, atau null sambil menampilkan "Memuat…"/error lewat render().
 */
export function useViewData<T>(key: unknown[], url: string) {
  return useQuery({ queryKey: ['view', ...key], queryFn: () => api<T>('GET', url), gcTime: 0, staleTime: 0 });
}

/** Beberapa GET sekaligus untuk satu halaman (sama dengan Promise.all v1); tanpa cache lama seperti useViewData. */
export function useViewAll<T extends unknown[]>(key: unknown[], urls: string[]) {
  return useQuery({
    queryKey: ['view', ...key],
    queryFn: () => Promise.all(urls.map((u) => api('GET', u))) as Promise<T>,
    gcTime: 0,
    staleTime: 0,
  });
}

/** Pembungkus: tampilkan Memuat…/error sampai data siap, lalu render children(data). */
export function WithData<T>({ q, children }: { q: ReturnType<typeof useViewData<T>>; children: (d: T) => ReactNode }) {
  if (q.isPending) return <Loading />;
  if (q.isError) return <ViewError error={q.error} />;
  return <>{children(q.data as T)}</>;
}
